import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SITE } from "@/config/site";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { name: "robots", content: "index, follow" },
      { title: "Privacy Policy · Surer Adda With Sanchayeeta" },
      { name: "description", content: "How Surer Adda With Sanchayeeta collects, uses and protects your personal information." },
      { property: "og:title", content: "Privacy Policy · Surer Adda" },
      { property: "og:description", content: "Our privacy practices — what data we collect, how we use it, and your rights." },
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/privacy` }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <p className="font-script text-4xl text-primary">Privacy Policy</p>
        <p className="mt-1 text-sm text-muted-foreground">Last updated: June 2026</p>

        <div className="prose prose-neutral mt-8 max-w-none text-foreground text-sm leading-relaxed">
          <p>
            At Surer Adda With Sanchayeeta, accessible from{" "}
            <a className="text-primary hover:underline" href={SITE.url}>www.sureraddawithsanchayeeta.in</a>,
            the privacy of our visitors is important to us. This Privacy Policy explains what types
            of information may be collected through our website and how that information may be used.
          </p>
          <p>By using this website, you agree to the terms of this Privacy Policy.</p>

          <h2 className="mt-6 text-lg font-semibold">Information We May Collect</h2>
          <p>
            We may collect limited personal and non-personal information when you visit or interact
            with our website. This may include:
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Name and contact details submitted through enquiry or contact forms</li>
            <li>Email address provided for communication purposes</li>
            <li>Event-related details shared for booking enquiries</li>
            <li>Browser type, device type, IP address, and general website usage data</li>
            <li>Cookies and analytics information used to improve website performance</li>
          </ul>
          <p>
            We only collect information that is necessary for communication, service-related
            enquiries, website improvement, and visitor experience.
          </p>

          <h2 className="mt-6 text-lg font-semibold">How We Use Your Information</h2>
          <p>The information collected through this website may be used for the following purposes:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>To respond to contact or booking enquiries</li>
            <li>To communicate regarding performances, events, or requested services</li>
            <li>To improve website functionality, content quality, and user experience</li>
            <li>To understand visitor behavior and website traffic trends</li>
            <li>To maintain website security and prevent misuse</li>
          </ul>
          <p>We do not sell, rent, or trade your personal information to third parties.</p>

          <h2 className="mt-6 text-lg font-semibold">Cookies</h2>
          <p>
            Our website may use cookies to improve user experience, analyze website traffic, and
            understand how visitors interact with our content. Cookies are small files stored on
            your device that help websites function more effectively.
          </p>
          <p>
            You may choose to disable cookies through your browser settings if you prefer. However,
            some features of the website may not function properly without them.
          </p>

          <h2 className="mt-6 text-lg font-semibold">Google AdSense and Third-Party Advertising</h2>
          <p>
            We may use third-party advertising services such as Google AdSense to display
            advertisements on this website. These services may use cookies and similar technologies
            to serve ads based on your visit to this website and other websites on the internet.
          </p>
          <p>
            Google may use the DART cookie to serve ads to users based on their browsing behavior.
            Users may choose to opt out of personalized advertising by visiting{" "}
            <a className="text-primary hover:underline" href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">
              Google Ads Settings
            </a>.
          </p>
          <p>
            Please note that third-party advertisers may have their own privacy policies regarding
            how they collect and use data. We encourage users to review those policies separately
            if needed.
          </p>

          <h2 className="mt-6 text-lg font-semibold">Third-Party Links</h2>
          <p>
            Our website may contain links to third-party websites, social media platforms, or
            external pages for informational or promotional purposes. We are not responsible for
            the privacy practices, content, or policies of those external websites. Visitors should
            review the privacy policies of third-party websites before sharing any personal
            information.
          </p>

          <h2 className="mt-6 text-lg font-semibold">Data Security</h2>
          <p>
            We take reasonable steps to protect the information shared through our website. However,
            no method of internet transmission or digital storage is completely secure. While we
            strive to protect user information, we cannot guarantee absolute security.
          </p>

          <h2 className="mt-6 text-lg font-semibold">Children's Privacy</h2>
          <p>
            This website is not intentionally directed toward children under the age of 13, and we
            do not knowingly collect personal information from children. If a parent or guardian
            believes that a child has submitted personal information through this website, they may
            contact us and we will take reasonable steps to remove that information.
          </p>

          <h2 className="mt-6 text-lg font-semibold">Your Consent</h2>
          <p>
            By using Surer Adda With Sanchayeeta, you consent to our Privacy Policy and agree to
            its terms.
          </p>

          <h2 className="mt-6 text-lg font-semibold">Updates to This Privacy Policy</h2>
          <p>
            We may update or revise this Privacy Policy from time to time to reflect changes in
            website practices, legal requirements, or service improvements. Any updates will be
            posted on this page with revised content.
          </p>

          <h2 className="mt-6 text-lg font-semibold">Contact Us</h2>
          <p>
            If you have any questions regarding this Privacy Policy, you may contact us through the
            details available on our{" "}
            <a className="text-primary hover:underline" href="/contact">Contact Us</a> page.
          </p>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
